import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

// ================================================
// COLABORADOR SELF QUERY HOOKS
// Para dados editáveis pelo próprio usuário
// ================================================

// Tipo específico para dados self (editáveis pelo usuário)
export interface CollaboratorSelf {
    // Identificação (necessário para compatibilidade)
    id?: string;
    name?: string;
    email?: string;

    // Dados pessoais básicos
    personalEmail?: string | null;
    phone?: string | null;
    birthDate?: string | null;
    nationality?: string | null;
    naturality?: string | null;
    birthState?: string | null;
    sex?: 'Masculino' | 'Feminino' | 'Outro' | null;
    gender?: string | null;
    race?: 'Branca' | 'Preta' | 'Parda' | 'Amarela' | 'Indígena' | 'Outra' | null;

    // Endereços
    addresses?: any[];
    isMailingSameAsMain?: boolean;

    // Informações de identidade
    cpf?: string | null;
    identityDocument?: any;

    // Contato de emergência
    emergencyContact?: any;

    // Informações familiares
    maritalStatus?: string | null;
    spouseName?: string | null;
    fatherName?: string | null;
    motherName?: string | null;
    children?: any[];

    // Informações bancárias
    salaryAccount?: {
        bankCode?: string;
        agency?: string;
        account?: string;
        digit?: string;
        pixKey?: string;
    } | null;
    personalAccount?: {
        bankCode?: string;
        agency?: string;
        account?: string;
        digit?: string;
        pixKey?: string;
    } | null;

    // Status de atualização do wizard de perfil
    updatedStatus?: {
        byUser?: {
            status?: 'PENDING' | 'IN_PROGRESS' | 'UPDATED';
            currentStep?: number;
        };
    };

    // Metadados de controle
    _metadata?: {
        lastUpdatedBy?: 'user' | 'admin_sync' | 'system';
        lastUpdatedAt?: any;
        version?: number;
        syncedFromPrivate?: boolean;
        originalUpdatedAt?: any;
    };
}

// Query keys para colaborador self
export const collaboratorSelfQueryKeys = {
    all: ['collaborators_self'] as const,
    detail: (id: string) => ['collaborators_self', id] as const,
};

/**
 * Hook para buscar dados self de um colaborador específico
 * Use quando o usuário estiver editando seus próprios dados
 */
export function useCollaboratorSelfQuery(collaboratorId: string) {
    return useQuery<CollaboratorSelf | null>({
        queryKey: collaboratorSelfQueryKeys.detail(collaboratorId),
        queryFn: async () => {
            if (!collaboratorId) return null;

            console.log(`[self-query] Fetching self data for ${collaboratorId}`);
            const docRef = doc(db, 'collaborators_self', collaboratorId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as CollaboratorSelf;
            } else {
                console.log(`[self-query] No self data found for ${collaboratorId}`);
                return null;
            }
        },
        enabled: !!collaboratorId,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

/**
 * Hook para atualizar dados self de um colaborador
 * Marca automaticamente como 'user' para triggers funcionarem
 */
export function useUpdateCollaboratorSelfMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ collaboratorId, data }: {
            collaboratorId: string;
            data: Partial<CollaboratorSelf>
        }) => {
            console.log(`[self-mutation] Updating self data for ${collaboratorId}`);

            const docRef = doc(db, 'collaborators_self', collaboratorId);

            // Adicionar metadados de controle para triggers
            const updateData = {
                ...data,
                _metadata: {
                    ...data._metadata,
                    lastUpdatedBy: 'user' as const,
                    lastUpdatedAt: new Date(),
                    version: (data._metadata?.version || 0) + 1,
                }
            };

            await updateDoc(docRef, updateData);
            return updateData;
        },
        onSuccess: (_, { collaboratorId }) => {
            // Invalidar cache para refetch
            queryClient.invalidateQueries({
                queryKey: collaboratorSelfQueryKeys.detail(collaboratorId)
            });

            console.log(`[self-mutation] Successfully updated self data for ${collaboratorId}`);
        },
        onError: (error, { collaboratorId }) => {
            console.error(`[self-mutation] Error updating self data for ${collaboratorId}:`, error);
        }
    });
}

/**
 * Hook para verificar se colaborador tem dados self inicializados
 */
export function useCollaboratorSelfExistsQuery(collaboratorId: string) {
    return useQuery<boolean>({
        queryKey: [...collaboratorSelfQueryKeys.detail(collaboratorId), 'exists'],
        queryFn: async () => {
            if (!collaboratorId) return false;

            const docRef = doc(db, 'collaborators_self', collaboratorId);
            const docSnap = await getDoc(docRef);

            return docSnap.exists();
        },
        enabled: !!collaboratorId,
        staleTime: 10 * 60 * 1000, // 10 minutos
    });
} 